import { useState } from "react";
import { ErrorCode, FileRejection, useDropzone } from "react-dropzone";
import { ChartOfAccountsTemplateUploadSource } from "@common/constants/domains/accounting/chartOfAccountsTemplateAccount";
import {
    MAX_FINANCIAL_DOCUMENT_FILE_SIZE_BYTES,
    MAX_FINANCIAL_DOCUMENT_FILE_SIZE_MB,
} from "@common/constants/financial-documents";
import {
    Modal,
    StandardModalBody,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { FileDropzone } from "@design-system/FileDropzone/FileDropzone";
import { Button } from "@design-system/Button/Button";
import { Link } from "@design-system/Link/Link";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { DownloadIcon } from "@icons";
import {
    chartOfAccountsTemplateUploadErrorMessage,
    useParseChartOfAccountsTemplateUploadMutation,
} from "@api/coaTemplateUpload.api";
import {
    getUploadSourceConfig,
    KICK_TEMPLATE_DOWNLOAD_URL,
    KICK_TEMPLATE_FILENAME,
} from "./uploadSources";
import { ChartOfAccountsTemplateUploadDraft } from "./useChartOfAccountsTemplateUploadFlow";
import styles from "./ChartOfAccountsTemplateUpload.module.scss";

interface Props {
    title: string;
    source: ChartOfAccountsTemplateUploadSource;
    draft: ChartOfAccountsTemplateUploadDraft | null;
    onClose: () => void;
    onDraftChange: (draft: ChartOfAccountsTemplateUploadDraft | null) => void;
    onNext: (draft: ChartOfAccountsTemplateUploadDraft) => void;
}

export const ChartOfAccountsTemplateUploadModal = ({
    title,
    source,
    draft,
    onClose,
    onDraftChange,
    onNext,
}: Props) => {
    const { label, fileTypesLabel, accept } = getUploadSourceConfig(source);
    const isKickTemplate =
        source === ChartOfAccountsTemplateUploadSource.kickTemplate;
    const parseUpload = useParseChartOfAccountsTemplateUploadMutation();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const rejectionMessage = (rejections: FileRejection[]) => {
        const code = rejections[0]?.errors[0]?.code;
        if (code === ErrorCode.FileTooLarge) {
            return `Max allowed size is ${MAX_FINANCIAL_DOCUMENT_FILE_SIZE_MB}MB.`;
        }
        if (code === ErrorCode.TooManyFiles) {
            return "Drop one file at a time.";
        }
        return `Upload a ${fileTypesLabel} file.`;
    };

    const handleDrop = (acceptedFiles: File[], rejections: FileRejection[]) => {
        onDraftChange(null);
        const [file] = acceptedFiles;
        if (!file) {
            setErrorMessage(
                rejections.length > 0 ? rejectionMessage(rejections) : null,
            );
            return;
        }
        setErrorMessage(null);
        parseUpload.mutate(
            { file, source },
            {
                onSuccess: (chart) =>
                    onDraftChange({ source, fileName: file.name, chart }),
                onError: (error) =>
                    setErrorMessage(
                        chartOfAccountsTemplateUploadErrorMessage({ error }),
                    ),
            },
        );
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: handleDrop,
        accept,
        multiple: false,
        maxSize: MAX_FINANCIAL_DOCUMENT_FILE_SIZE_BYTES,
        disabled: parseUpload.isPending,
    });

    const dropzoneTitle = (() => {
        if (parseUpload.isPending) {
            return "Reading your file…";
        }
        return draft ? draft.fileName : "Upload or drag and drop your file";
    })();

    const description = isKickTemplate
        ? `Import your accounts from a ${fileTypesLabel} file. Download the Kick template below to get the format right.`
        : `Upload the Chart of Accounts file you exported from ${label}.`;
    const dropzoneHint = (() => {
        if (draft) {
            return "Drop another file to replace it";
        }
        if (isKickTemplate) {
            return "Your filled-in Kick template";
        }
        return `Accepts ${fileTypesLabel}`;
    })();

    return (
        <Modal show onClose={onClose} size="lg">
            <StandardModalHeader>{title}</StandardModalHeader>
            <StandardModalBody>
                <p className={styles.description}>{description}</p>
                <FileDropzone
                    rootProps={getRootProps()}
                    isDragActive={isDragActive}
                    className={styles.dropzone}
                    cover={<span>Drop your file to upload</span>}
                    coverClassName={styles.dropCover}
                >
                    <input {...getInputProps()} />
                    <span className={styles.dropzoneTitle}>
                        {dropzoneTitle}
                    </span>
                    <span className={styles.dropzoneHint}>{dropzoneHint}</span>
                </FileDropzone>
                {isKickTemplate && (
                    <Tooltip content="Spreadsheet with the columns Kick reads: code, account name, type, and up to 5 groups. Only name and type are required. We recommend enabling macros when you open it so Excel checks your rows as you type.">
                        <Link
                            className={styles.templateDownload}
                            leftIcon={<DownloadIcon />}
                            href={KICK_TEMPLATE_DOWNLOAD_URL}
                            download={KICK_TEMPLATE_FILENAME}
                        >
                            Download Kick template
                        </Link>
                    </Tooltip>
                )}
                {errorMessage && (
                    <p role="alert" className={styles.error}>
                        {errorMessage}
                    </p>
                )}
            </StandardModalBody>
            <StandardModalFooter>
                <Button
                    variant="primary"
                    size="xl"
                    disabled={!draft}
                    onClick={() => {
                        if (draft) {
                            onNext(draft);
                        }
                    }}
                >
                    Next
                </Button>
            </StandardModalFooter>
        </Modal>
    );
};
